// Area: branding (owner design-system, plan §9.1). The workspace brand kit: logos (light background,
// dark background, mark), brand colours, brand fonts (uploaded WOFF2/WOFF/TTF/OTF with a licence
// confirmation), and the default theme of new decks, document templates and public pages. Saving the kit
// regenerates the brand theme (`BRAND_THEME_KEY`) in the workspace's themes. Routes live in the API's
// `themes` module.
import { z } from 'zod';
import { BrandKit, Hex, Theme } from '@dataroom/core/doc/theme';
import { ThemeDTO } from '../artefacts/themes';
import { defineContract } from '../define';

export * from './messages';
export {
  BRAND_LOGO_VARIANTS,
  BRAND_SURFACES,
  BRAND_THEME_KEY,
  BrandKit,
  BrandSurface,
} from '@dataroom/core/doc/theme';
export type { BrandKitInput, BrandLogoVariant } from '@dataroom/core/doc/theme';

/** The largest font file the brand kit accepts (the API's body limit leaves room for base64). */
export const MAX_BRAND_FONT_BYTES = 3_000_000;
/** Base64 of the largest font file. */
const MAX_BRAND_FONT_BASE64 = Math.ceil(MAX_BRAND_FONT_BYTES / 3) * 4;

export const BRAND_FONT_FORMATS = ['woff2', 'woff', 'truetype', 'opentype'] as const;
export const BrandFontFormat = z.enum(BRAND_FONT_FORMATS);
export type BrandFontFormat = z.infer<typeof BrandFontFormat>;

/**
 * What the font's own licence flags (OpenType `OS/2.fsType`) allow: `installable` and `editable`
 * fonts can be embedded anywhere, `preview` fonts in documents that are viewed and printed;
 * `restricted` fonts are refused.
 */
export const FontEmbedding = z.enum(['installable', 'editable', 'preview', 'restricted']);
export type FontEmbedding = z.infer<typeof FontEmbedding>;

export const FontWeight = z.number().int().min(100).max(900).multipleOf(100);
export const FontStyle = z.enum(['normal', 'italic']);
export type FontStyle = z.infer<typeof FontStyle>;

const Person = z.object({ id: z.uuid(), name: z.string() });

export const BrandFontDTO = z.object({
  id: z.uuid(),
  /** Family slug: the stored font value is `custom:<slug>`. */
  slug: z.string(),
  /** The value documents and themes store (`custom:acme-sans`). */
  value: z.string(),
  /** Display name (`Acme Sans`). */
  family: z.string(),
  weight: FontWeight,
  style: FontStyle,
  format: BrandFontFormat,
  fileName: z.string(),
  bytes: z.number().int().nonnegative(),
  embedding: FontEmbedding,
  licenceConfirmedBy: Person.nullable(),
  licenceConfirmedAt: z.string(),
  createdAt: z.string(),
  /** The font file (session-authenticated, cacheable forever: a face never changes). */
  url: z.string(),
});
export type BrandFontDTO = z.infer<typeof BrandFontDTO>;

export const BrandLogoDTO = z.object({
  assetId: z.uuid(),
  name: z.string(),
  mime: z.string(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  /** The image (session-authenticated). */
  url: z.string(),
});
export type BrandLogoDTO = z.infer<typeof BrandLogoDTO>;

export const BrandKitDTO = z.object({
  kit: BrandKit,
  /** False until an administrator saves the kit for the first time. */
  exists: z.boolean(),
  /** 0 before the first save; send it back with `updateBrandKit`. */
  version: z.number().int().nonnegative(),
  updatedAt: z.string().nullable(),
  updatedBy: Person.nullable(),
  canEdit: z.boolean(),
  logos: z.object({
    primary: BrandLogoDTO.optional(),
    onDark: BrandLogoDTO.optional(),
    mark: BrandLogoDTO.optional(),
  }),
  fonts: z.array(BrandFontDTO),
  /** The brand theme the kit maintains (null before the first save). */
  theme: ThemeDTO.nullable(),
  /** `@font-face` rules of every uploaded face (link it once per page; empty when there is none). */
  fontsCssUrl: z.string(),
  /**
   * The brand as pages published to the web load it (`getPublicBrand`; no session); null before the
   * first save.
   */
  publicUrl: z.string().nullable(),
});
export type BrandKitDTO = z.infer<typeof BrandKitDTO>;

export const getBrandKit = defineContract({
  method: 'GET',
  path: '/brand-kit',
  response: BrandKitDTO,
  summary: 'The workspace brand kit: logos, colours, fonts and default themes',
});

export const UpdateBrandKitBody = z
  .object({
    /** The version you edited (0 for the first save). */
    version: z.number().int().nonnegative(),
    kit: BrandKit,
  })
  .strict();
export type UpdateBrandKitBody = z.input<typeof UpdateBrandKitBody>;

export const updateBrandKit = defineContract({
  method: 'PUT',
  path: '/brand-kit',
  body: UpdateBrandKitBody,
  response: BrandKitDTO,
  summary: 'Save the brand kit and regenerate the brand theme',
});

export const UploadBrandFontBody = z
  .object({
    fileName: z.string().trim().min(1).max(160),
    /** The file, base64 (WOFF2, WOFF, TTF or OTF). */
    data: z.string().min(1).max(MAX_BRAND_FONT_BASE64),
    /** Family name to file it under; default: the name inside the font. */
    family: z.string().trim().min(1).max(80).optional(),
    /** Default: the weight and style inside the font. */
    weight: FontWeight.optional(),
    style: FontStyle.optional(),
    /** The uploader confirms the font's licence allows this use. */
    licenceConfirmed: z.literal(true),
  })
  .strict();
export type UploadBrandFontBody = z.infer<typeof UploadBrandFontBody>;

export const uploadBrandFont = defineContract({
  method: 'POST',
  path: '/brand-kit/fonts',
  body: UploadBrandFontBody,
  response: BrandFontDTO,
  status: 201,
  summary: 'Upload a brand font face (WOFF2, WOFF, TTF or OTF)',
});

export const UpdateBrandFontBody = z
  .object({
    family: z.string().trim().min(1).max(80).optional(),
    weight: FontWeight.optional(),
    style: FontStyle.optional(),
  })
  .strict()
  .refine((b) => b.family !== undefined || b.weight !== undefined || b.style !== undefined, {
    message: 'Change the family, weight or style',
  });

export const updateBrandFont = defineContract({
  method: 'PATCH',
  path: '/brand-kit/fonts/:id',
  params: z.object({ id: z.uuid() }),
  body: UpdateBrandFontBody,
  response: BrandFontDTO,
  summary: 'Rename a brand font family, or correct a face’s weight or style',
});

export const deleteBrandFont = defineContract({
  method: 'DELETE',
  path: '/brand-kit/fonts/:id',
  params: z.object({ id: z.uuid() }),
  response: z.void(),
  summary: 'Remove a brand font face',
});

export const brandFontFile = defineContract({
  method: 'GET',
  path: '/brand-kit/fonts/:id/file',
  params: z.object({ id: z.uuid() }),
  // Binary font body (the handler sends it itself).
  response: z.unknown(),
  summary: 'Brand font file',
});

export const brandFontsCss = defineContract({
  method: 'GET',
  path: '/brand-kit/fonts.css',
  // text/css (the handler sends it itself).
  response: z.unknown(),
  summary: '@font-face rules of the workspace’s brand fonts',
});

// -- public (pages published to the web) --------------------------------------------------------

const PublicKey = z.string().regex(/^[a-f0-9]{32}$/);

export const PublicBrandDTO = z.object({
  /** The workspace's name (the logo's alternative text). */
  name: z.string(),
  logos: z.object({
    primary: z
      .object({ url: z.string(), width: z.number().nullable(), height: z.number().nullable() })
      .optional(),
    onDark: z
      .object({ url: z.string(), width: z.number().nullable(), height: z.number().nullable() })
      .optional(),
    mark: z
      .object({ url: z.string(), width: z.number().nullable(), height: z.number().nullable() })
      .optional(),
  }),
  colors: z.array(z.object({ name: z.string(), hex: Hex })),
  /** The theme public pages use (the kit's public-page default, else the brand theme). */
  theme: Theme.nullable(),
  /**
   * The accent app-like public surfaces (sign-in, forms) paint buttons and focus in: the kit's accent
   * while its `app.accent` is on, else null (they keep the product accent).
   */
  appAccent: Hex.nullable().optional(),
  fontsCssUrl: z.string(),
});
export type PublicBrandDTO = z.infer<typeof PublicBrandDTO>;

export const getPublicBrand = defineContract({
  method: 'GET',
  path: '/public/brand/:key',
  params: z.object({ key: PublicKey }),
  query: z.object({ lang: z.string().max(20).optional() }),
  response: PublicBrandDTO,
  public: true,
  summary: 'The brand a public page shows (logos, colours, fonts and theme)',
});

export const publicBrandAsset = defineContract({
  method: 'GET',
  path: '/public/brand/:key/logos/:variant',
  params: z.object({ key: PublicKey, variant: z.enum(['primary', 'onDark', 'mark']) }),
  response: z.unknown(),
  public: true,
  summary: 'A logo of a public brand',
});

export const publicBrandFontFile = defineContract({
  method: 'GET',
  path: '/public/brand/:key/fonts/:id',
  params: z.object({ key: PublicKey, id: z.uuid() }),
  response: z.unknown(),
  public: true,
  summary: 'A font file of a public brand',
});

export const publicBrandFontsCss = defineContract({
  method: 'GET',
  path: '/public/brand/:key/fonts.css',
  params: z.object({ key: PublicKey }),
  response: z.unknown(),
  public: true,
  summary: '@font-face rules of a public brand',
});
